import React, { useState, useEffect, useRef } from 'react';
import QrScanner from 'react-qr-scanner';
import api from '../services/api';

export default function ReturnTab() {
  const [mode, setMode] = useState('manual'); // 'manual' | 'scan'
  const [records, setRecords] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [scanResult, setScanResult] = useState('');
  const [loading, setLoading] = useState(false);

  // لمنع تكرار نفس المسح بسرعة
  const lastScanValueRef = useRef('');
  const lastScanTimeRef = useRef(0);
  const SCAN_COOLDOWN_MS = 1200;

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await api.get('/borrowings');
      const data = Array.isArray(res.data) ? res.data : (res.data?.borrowings || []);
      // نعرض فقط الإعارات غير المُعادة
      setRecords(data.filter(r => r && !r.returnStatus));
    } catch (err) {
      console.error(err);
      alert('❌ تعذر تحميل السجلات الحالية');
    } finally {
      setLoading(false);
    }
  };

  const handleManualReturn = async (e) => {
    e.preventDefault();
    if (!selectedId) return alert('⚠️ حدد سجلًا للإرجاع');
    try {
      await api.put(`/borrowings/${selectedId}`, { returnStatus: true });
      setSelectedId('');
      await fetchRecords();
      alert('✅ تم إرجاع الكتاب بنجاح');
    } catch (err) {
      console.error(err);
      alert('❌ فشل في إرجاع الكتاب');
    }
  };

  const resolveMatchByCode = (code) => {
    // نطابق ID الكتاب أو الباركود المخزّن في الكتاب أو في سجل الاستعارة
    return records.find(r =>
      r?.book?._id === code ||
      r?.book?.barcode === code ||
      r?.barcode === code
    );
  };

  const handleScan = async (raw) => {
    if (!raw) return;
    const value = raw.text || raw;
    const now = Date.now();

    // امنع التكرار خلال مهلة قصيرة أو إذا نفس القيمة
    if (lastScanValueRef.current === value && now - lastScanTimeRef.current < SCAN_COOLDOWN_MS) {
      return;
    }
    lastScanValueRef.current = value;
    lastScanTimeRef.current = now;

    setScanResult(value);

    const match = resolveMatchByCode(String(value).trim());
    if (!match) {
      alert('⚠️ لا يوجد سجل إعارة نشط لهذا الكتاب');
      return;
    }

    try {
      await api.put(`/borrowings/${match._id}`, { returnStatus: true });
      alert(`✅ تم إرجاع الكتاب "${match.book?.name || match.book?.title || ''}" للمستخدم ${match.user?.name || ''}`);
      await fetchRecords();
      setScanResult('');
    } catch (err) {
      console.error(err);
      alert('❌ فشل في الإرجاع عبر المسح');
    }
  };

  const handleError = (err) => {
    console.error('خطأ في الماسح:', err);
  };

  const fmtDate = (v) => {
    if (!v) return '—';
    try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  // ستايل الماسح + تفضيل الكاميرا الخلفية على الموبايل
  const previewStyle = { width: '100%', height: '100%', objectFit: 'cover' };
  const videoConstraints = { facingMode: 'environment' };

  return (
    <div className="container mx-auto max-w-5xl px-4 py-6 space-y-8" dir="rtl">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-black mb-2">
        🔄 إرجاع الكتب
      </h2>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 bg-blue-50 rounded-full overflow-hidden shadow-inner w-fit mx-auto p-1">
        {['manual', 'scan'].map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-5 sm:px-6 py-2 font-semibold transition duration-300 text-sm md:text-base rounded-full focus:outline-none ${
              mode === m
                ? 'bg-blue-600 text-white shadow'
                : 'text-blue-800 hover:bg-blue-100'
            }`}
          >
            {m === 'manual' ? '📝 يدوي' : '📷 مسح'}
          </button>
        ))}
      </div>

      {/* Manual Return */}
      {mode === 'manual' && (
        <form
          onSubmit={handleManualReturn}
          className="bg-white rounded-2xl shadow p-5 sm:p-8 space-y-4 sm:space-y-6"
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-700">📚 اختر سجل الإعارة</label>
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              required
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">— اختر —</option>
              {records.map(r => (
                <option key={r._id} value={r._id}>
                  {r?.user?.name || 'مستخدم'} — “{r?.book?.name || r?.book?.title || 'كتاب'}” | تاريخ الإرجاع: {fmtDate(r?.dueDate)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition"
            disabled={!selectedId}
          >
            ✅ إرجاع الكتاب
          </button>
        </form>
      )}

      {/* Scan Return */}
      {mode === 'scan' && (
        <div className="bg-white rounded-2xl shadow p-5 sm:p-8 space-y-5 text-center">
          <p className="text-gray-700 text-base sm:text-lg font-medium">
            📷 امسح كود الكتاب (ID أو باركود) لإرجاعه
          </p>

          {/* حاوية ماسح مرنة بنسبة 4:3 */}
          <div className="w-full max-w-md mx-auto rounded-lg overflow-hidden border">
            <div className="relative" style={{ paddingTop: '75%' }}>
              <div className="absolute inset-0">
                <QrScanner
                  delay={300}
                  style={previewStyle}
                  constraints={{ video: videoConstraints }}
                  onError={handleError}
                  onScan={(result) => result && handleScan(result.text || result)}
                />
              </div>
            </div>
          </div>

          {scanResult && (
            <p className="text-sm text-gray-600">
              آخر مسح: <strong className="text-blue-700 break-all">{scanResult}</strong>
            </p>
          )}
        </div>
      )}

      {/* Active Records */}
      <div className="bg-white rounded-2xl shadow p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-lg sm:text-xl font-semibold text-blue-800">📌 الإعارات النشطة</h3>
          <button
            onClick={fetchRecords}
            className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
          >
            🔁 تحديث
          </button>
        </div>

        {loading && <p className="text-gray-600">⏳ تحميل...</p>}

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[28rem] overflow-auto pr-1">
          {!loading && records.length === 0 && (
            <li className="text-gray-400">لا توجد إعارات نشطة حاليًا.</li>
          )}

          {records.map((r) => (
            <li
              key={r._id}
              className="border rounded-lg p-3 sm:p-4 text-gray-700 bg-gray-50 hover:bg-gray-100 transition"
            >
              <div className="font-semibold">{r?.user?.name || 'مستخدم'}</div>
              <div className="text-sm mt-0.5">“{r?.book?.name || r?.book?.title || 'كتاب'}”</div>
              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                يستحق الإرجاع بتاريخ {fmtDate(r?.dueDate)}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    try {
                      await api.put(`/borrowings/${r._id}`, { returnStatus: true });
                      await fetchRecords();
                      alert('✅ تم إرجاع الكتاب');
                    } catch (err) {
                      console.error(err);
                      alert('❌ فشل في الإرجاع');
                    }
                  }}
                  className="px-3 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white text-sm"
                >
                  إرجاع الآن
                </button>
                <span className="px-2 py-1 rounded bg-white border text-xs text-gray-600">
                  📅 {todayStr}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
